import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useCookieConsent } from '../../contexts/cookie-consent-context';
import { LogoMark } from '../brand/logo-mark';

import { Container } from './container';

function FooterColumn({ heading, links }: { heading: string; links: Array<{ to: string; label: string }> }) {
  return (
    <div>
      <p className="text-label font-medium text-ink">{heading}</p>
      <ul className="mt-3 space-y-2">
        {links.map((link) => (
          <li key={link.to}>
            <Link to={link.to} className="text-body text-muted transition-colors hover:text-ink">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  const { openPreferences } = useCookieConsent();
  const { t } = useTranslation('marketing');
  const year = new Date().getFullYear();

  const productLinks = [
    { to: '/', label: t('footer.links.home') },
    { to: '/features', label: t('footer.links.features') },
    { to: '/pricing', label: t('footer.links.pricing') },
    { to: '/developers', label: t('footer.links.developers') },
    { to: '/download', label: t('footer.links.desktopApp') },
    { to: '/help/mobile', label: t('footer.links.mobileApp') },
  ];

  const supportLinks = [
    { to: '/help', label: t('footer.links.helpCenter') },
    { to: '/help/faq', label: t('footer.links.faq') },
    { to: '/contact', label: t('footer.links.contactUs') },
  ];

  const legalLinks = [
    { to: '/legal/privacy', label: t('footer.links.privacyPolicy') },
    { to: '/legal/cookies', label: t('footer.links.cookiePolicy') },
    { to: '/legal/terms', label: t('footer.links.termsOfService') },
    { to: '/legal/impressum', label: t('footer.links.impressum') },
    { to: '/legal/account-deletion', label: t('footer.links.accountDeletion') },
    { to: '/legal/dpa', label: t('footer.links.dpa') },
    { to: '/legal/sub-processors', label: t('footer.links.subProcessors') },
  ];

  return (
    <footer className="border-t border-border bg-bg">
      <Container className="py-12 md:py-16">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2" aria-label={t('footer.homeAriaLabel')}>
              <LogoMark />
              <span className="text-title text-ink">{t('brand.name')}</span>
            </Link>
            <p className="mt-3 max-w-xs text-body text-muted">{t('footer.tagline')}</p>
          </div>
          <FooterColumn heading={t('footer.columns.product')} links={productLinks} />
          <FooterColumn heading={t('footer.columns.support')} links={supportLinks} />
          <FooterColumn heading={t('footer.columns.legal')} links={legalLinks} />
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-border pt-6 text-body text-muted md:flex-row md:items-center md:justify-between">
          <p>{t('footer.copyright', { year })}</p>
          <button
            type="button"
            onClick={openPreferences}
            className="text-left underline-offset-4 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            {t('footer.cookiePreferences')}
          </button>
        </div>
      </Container>
    </footer>
  );
}
